import React, { useCallback, useEffect, useState } from 'react';
import { Button, DatePicker, Input, Modal, Space, Table, Tabs, Tag, message } from 'antd';
import { CheckOutlined, CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { approveWithdrawal, getWithdrawals, rejectWithdrawal } from '../../api/withdrawals';

const { TextArea } = Input;

const statusMap = {
  PENDING: { label: '待审核', color: 'orange' },
  APPROVED: { label: '已批准', color: 'blue' },
  REJECTED: { label: '已拒绝', color: 'red' },
  COMPLETED: { label: '已完成', color: 'green' },
  FAILED: { label: '失败', color: 'red' },
};

const Withdrawals = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUserId = searchParams.get('userId') || '';
  const requestedStatus = searchParams.get('status');
  const initialStatus = requestedStatus === 'ALL' ? '' : requestedStatus || 'PENDING';
  const initialDateFilter = {
    dateFrom: searchParams.get('dateFrom') || '',
    dateTo: searchParams.get('dateTo') || '',
  };

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [userIdInput, setUserIdInput] = useState(initialUserId);
  const [appliedUserId, setAppliedUserId] = useState(initialUserId);
  const [dateFilter, setDateFilter] = useState(initialDateFilter);
  const currentPage = pagination.current;
  const currentPageSize = pagination.pageSize;

  const [rejectVisible, setRejectVisible] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getWithdrawals({
        page: currentPage,
        size: currentPageSize,
        status: statusFilter || undefined,
        userId: appliedUserId || undefined,
        dateFrom: dateFilter.dateFrom || undefined,
        dateTo: dateFilter.dateTo || undefined,
      });
      setData(result.list);
      setPagination((prev) => ({ ...prev, total: result.total }));
    } catch (error) {
      console.error('Failed to fetch withdrawals:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, currentPageSize, statusFilter, appliedUserId, dateFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const syncSearchParams = (nextStatus, nextUserId, nextDateFilter = dateFilter) => {
    const params = {};
    if (nextStatus) {
      params.status = nextStatus;
    }
    if (nextUserId) {
      params.userId = nextUserId;
    }
    if (nextDateFilter.dateFrom) {
      params.dateFrom = nextDateFilter.dateFrom;
    }
    if (nextDateFilter.dateTo) {
      params.dateTo = nextDateFilter.dateTo;
    }
    setSearchParams(params);
  };

  const handleUserSearch = () => {
    setAppliedUserId(userIdInput);
    setPagination((prev) => ({ ...prev, current: 1 }));
    syncSearchParams(statusFilter, userIdInput);
  };

  const withdrawalDateRange = dateFilter.dateFrom && dateFilter.dateTo
    ? [dayjs(dateFilter.dateFrom), dayjs(dateFilter.dateTo)]
    : null;

  const handleApprove = (id) => {
    Modal.confirm({
      title: '确认批准',
      content: '确定要批准该提现申请吗？',
      onOk: async () => {
        await approveWithdrawal(id);
        message.success('批准成功');
        fetchData();
      },
    });
  };

  const handleReject = (id) => {
    setRejectId(id);
    setRejectReason('');
    setRejectVisible(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      message.error('请输入拒绝原因');
      return;
    }
    setRejectLoading(true);
    try {
      await rejectWithdrawal(rejectId, { rejectReason });
      message.success('拒绝成功');
      setRejectVisible(false);
      fetchData();
    } catch (error) {
      console.error('Failed to reject withdrawal:', error);
    } finally {
      setRejectLoading(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    {
      title: '用户ID',
      dataIndex: 'userId',
      width: 120,
      render: (value) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/users?userId=${value}`)}>
          {value}
        </Button>
      ),
    },
    {
      title: '金额',
      dataIndex: 'amount',
      render: (value) => `¥${Number(value || 0).toFixed(2)}`,
    },
    {
      title: '处理优先级',
      dataIndex: 'memberPriority',
      width: 110,
      render: (memberPriority) => (
        memberPriority ? <Tag color="gold">会员优先</Tag> : '-'
      ),
    },
    {
      title: '渠道',
      dataIndex: 'channelType',
      render: (type) => (
        <Tag color={type === 'WECHAT' ? 'green' : 'blue'}>
          {type === 'WECHAT' ? '微信' : '支付宝'}
        </Tag>
      ),
    },
    { title: '账户名', dataIndex: 'accountName' },
    {
      title: '状态',
      dataIndex: 'status',
      render: (status) => {
        const item = statusMap[status] || { label: status, color: 'default' };
        return <Tag color={item.color}>{item.label}</Tag>;
      },
    },
    {
      title: '风险等级',
      dataIndex: 'riskLevel',
      render: (level) => {
        const colors = { LOW: 'green', MEDIUM: 'orange', HIGH: 'red' };
        const labels = { LOW: '低', MEDIUM: '中', HIGH: '高' };
        return <Tag color={colors[level]}>{labels[level] || level}</Tag>;
      },
    },
    {
      title: '申请时间',
      dataIndex: 'createdAt',
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '操作',
      render: (_, record) => (
        record.status === 'PENDING' ? (
          <Space>
            <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => handleApprove(record.id)}>
              批准
            </Button>
            <Button size="small" danger icon={<CloseOutlined />} onClick={() => handleReject(record.id)}>
              拒绝
            </Button>
          </Space>
        ) : record.rejectReason ? (
          <span style={{ color: '#999' }}>拒绝原因: {record.rejectReason}</span>
        ) : '-'
      ),
    },
  ];

  const tabItems = [
    { key: 'PENDING', label: '待审核' },
    { key: 'APPROVED', label: '已批准' },
    { key: 'REJECTED', label: '已拒绝' },
    { key: 'COMPLETED', label: '已完成' },
    { key: 'FAILED', label: '失败' },
    { key: '', label: '全部' },
  ];

  return (
    <div>
      <h2>提现审核</h2>

      <Tabs
        activeKey={statusFilter}
        items={tabItems}
        onChange={(key) => {
          setStatusFilter(key);
          setPagination((prev) => ({ ...prev, current: 1 }));
          syncSearchParams(key, appliedUserId);
        }}
      />

      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="用户ID"
          value={userIdInput}
          onChange={(e) => setUserIdInput(e.target.value)}
          onPressEnter={handleUserSearch}
          style={{ width: 140 }}
        />
        <DatePicker.RangePicker
          value={withdrawalDateRange}
          format="YYYY-MM-DD"
          placeholder={['申请开始日期', '申请结束日期']}
          onChange={(_, dateStrings) => {
            const nextDateFilter = {
              dateFrom: dateStrings?.[0] || '',
              dateTo: dateStrings?.[1] || '',
            };
            setDateFilter(nextDateFilter);
            setPagination((prev) => ({ ...prev, current: 1 }));
            syncSearchParams(statusFilter, appliedUserId, nextDateFilter);
          }}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleUserSearch}>
          筛选用户
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, pageSize) => setPagination({ current: page, pageSize, total: pagination.total }),
        }}
      />

      <Modal
        title="拒绝提现"
        open={rejectVisible}
        onCancel={() => setRejectVisible(false)}
        onOk={handleRejectSubmit}
        confirmLoading={rejectLoading}
      >
        <div style={{ marginBottom: 8 }}>请输入拒绝原因:</div>
        <TextArea
          rows={3}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="请输入拒绝原因"
        />
      </Modal>
    </div>
  );
};

export default Withdrawals;
